import { create } from "zustand";
import {
  StockCommitmentsClient,
  IStockCommitment,
  IStockAvailability,
  ICreateStockCommitment,
  IUpdateStockCommitment,
} from "../clients/StockCommitmentsClient";
import { useStockStore } from "@stores/stockStore";

interface StockCommitmentsStoreState {
  // State
  commitments: IStockCommitment[];
  stockAvailability: IStockAvailability[];
  loading: boolean;
  error: Error | null;

  // Actions
  getStockCommitmentsByOrderId: (orderId: string) => Promise<IStockCommitment[]>;
  getStockCommitmentsByOrderItemId: (
    orderItemId: string
  ) => Promise<IStockCommitment[]>;
  getStockCommitmentsByInventoryId: (
    inventoryId: string
  ) => Promise<IStockCommitment[]>;
  createStockCommitment: (
    commitment: ICreateStockCommitment
  ) => Promise<IStockCommitment>;
  updateStockCommitment: (
    commitment: IUpdateStockCommitment
  ) => Promise<IStockCommitment>;
  deleteStockCommitment: (id: string) => Promise<void>;
  deleteStockCommitmentsByOrderId: (orderId: string) => Promise<void>;
  deleteStockCommitmentsByOrderItemId: (orderItemId: string) => Promise<void>;

  // Stock availability
  getStockAvailability: (productId: string) => Promise<IStockAvailability>;
  getBulkStockAvailability: (
    productIds: string[]
  ) => Promise<IStockAvailability[]>;
  checkStockAvailability: (
    productId: string,
    requestedQuantity: number
  ) => Promise<{ available: boolean; availableQuantity: number; shortfall: number }>;

  // Stock operations
  commitStock: (
    orderId: string,
    orderItemId: string,
    productId: string,
    quantity: number,
    organizationId: string
  ) => Promise<IStockCommitment>;
  releaseStockCommitment: (commitmentId: string) => Promise<void>;
  releaseAllStockCommitmentsForOrder: (orderId: string) => Promise<void>;

  // Alerts
  getLowStockAlerts: (threshold?: number) => Promise<IStockAvailability[]>;

  // Utility
  clearError: () => void;
  refreshCommitments: () => Promise<void>;
}

export const useStockCommitmentsStore = create<StockCommitmentsStoreState>(
  (set, get) => ({
    // Initial state
    commitments: [],
    stockAvailability: [],
    loading: false,
    error: null,

    // Get stock commitments by order ID
    async getStockCommitmentsByOrderId(orderId: string) {
      set({ loading: true, error: null });
      try {
        const commitments =
          await StockCommitmentsClient.getStockCommitmentsByOrderId(orderId);

        // Update store with new commitments
        set((state) => ({
          ...state,
          commitments: [
            ...state.commitments.filter((c) => c.order_id !== orderId),
            ...commitments,
          ],
        }));

        return commitments;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Get stock commitments by order item ID
    async getStockCommitmentsByOrderItemId(orderItemId: string) {
      set({ loading: true, error: null });
      try {
        const commitments =
          await StockCommitmentsClient.getStockCommitmentsByOrderItemId(
            orderItemId
          );

        // Update store with new commitments
        set((state) => ({
          ...state,
          commitments: [
            ...state.commitments.filter((c) => c.order_item_id !== orderItemId),
            ...commitments,
          ],
        }));

        return commitments;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Get stock commitments by inventory ID
    async getStockCommitmentsByInventoryId(inventoryId: string) {
      set({ loading: true, error: null });
      try {
        const commitments =
          await StockCommitmentsClient.getStockCommitmentsByInventoryId(
            inventoryId
          );

        // Update store with new commitments
        set((state) => ({
          ...state,
          commitments: [
            ...state.commitments.filter((c) => c.inventory_id !== inventoryId),
            ...commitments,
          ],
        }));

        return commitments;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Create stock commitment
    async createStockCommitment(commitment: ICreateStockCommitment) {
      set({ loading: true, error: null });
      try {
        const newCommitment =
          await StockCommitmentsClient.createStockCommitment(commitment);

        // Add to store
        set((state) => ({
          ...state,
          commitments: [...state.commitments, newCommitment],
        }));

        return newCommitment;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Update stock commitment
    async updateStockCommitment(commitment: IUpdateStockCommitment) {
      set({ loading: true, error: null });
      try {
        const updatedCommitment =
          await StockCommitmentsClient.updateStockCommitment(commitment);

        // Update in store
        set((state) => ({
          ...state,
          commitments: state.commitments.map((c) =>
            c.id === commitment.id ? updatedCommitment : c
          ),
        }));

        return updatedCommitment;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Delete stock commitment
    async deleteStockCommitment(id: string) {
      set({ loading: true, error: null });
      try {
        await StockCommitmentsClient.deleteStockCommitment(id);

        // Remove from store
        set((state) => ({
          ...state,
          commitments: state.commitments.filter((c) => c.id !== id),
        }));
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Delete stock commitments by order ID
    async deleteStockCommitmentsByOrderId(orderId: string) {
      set({ loading: true, error: null });
      try {
        await StockCommitmentsClient.deleteStockCommitmentsByOrderId(orderId);

        // Remove from store
        set((state) => ({
          ...state,
          commitments: state.commitments.filter((c) => c.order_id !== orderId),
        }));
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Delete stock commitments by order item ID
    async deleteStockCommitmentsByOrderItemId(orderItemId: string) {
      set({ loading: true, error: null });
      try {
        await StockCommitmentsClient.deleteStockCommitmentsByOrderItemId(
          orderItemId
        );

        // Remove from store
        set((state) => ({
          ...state,
          commitments: state.commitments.filter(
            (c) => c.order_item_id !== orderItemId
          ),
        }));
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Get stock availability for a specific product
    async getStockAvailability(productId: string) {
      set({ loading: true, error: null });
      try {
        const availability =
          await StockCommitmentsClient.getStockAvailability(productId);

        // Update store
        set((state) => ({
          ...state,
          stockAvailability: [
            ...state.stockAvailability.filter(
              (a) => a.product_id !== availability.product_id
            ),
            availability,
          ],
        }));

        return availability;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Get bulk stock availability
    async getBulkStockAvailability(productIds: string[]) {
      set({ loading: true, error: null });
      try {
        const availability =
          await StockCommitmentsClient.getBulkStockAvailability(productIds);

        // Update store
        set((state) => ({
          ...state,
          stockAvailability: [
            ...state.stockAvailability.filter(
              (a) => !productIds.includes(a.product_id)
            ),
            ...availability,
          ],
        }));

        return availability;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Check stock availability
    async checkStockAvailability(productId: string, requestedQuantity: number) {
      try {
        return await StockCommitmentsClient.checkStockAvailability(
          productId,
          requestedQuantity
        );
      } catch (error) {
        set({ error: error as Error });
        throw error;
      }
    },

    // Commit stock
    async commitStock(
      orderId: string,
      orderItemId: string,
      productId: string,
      quantity: number,
      organizationId: string
    ) {
      set({ loading: true, error: null });
      try {
        const commitment = await StockCommitmentsClient.commitStock(
          orderId,
          orderItemId,
          productId,
          quantity,
          organizationId
        );

        // Add to store
        set((state) => ({
          ...state,
          commitments: [...state.commitments, commitment],
        }));

        // Refresh stock availability
        await get().getStockAvailability(productId);

        // Refresh stock store to reflect committed quantity changes
        const stockStore = useStockStore.getState();
        await stockStore.refreshStocksByProductId(productId);

        return commitment;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Release stock commitment
    async releaseStockCommitment(commitmentId: string) {
      set({ loading: true, error: null });
      try {
        // Get the commitment details before releasing
        const commitment = get().commitments.find((c) => c.id === commitmentId);
        if (!commitment) {
          throw new Error(`Commitment with ID ${commitmentId} not found`);
        }

        await StockCommitmentsClient.releaseStockCommitment(commitmentId);

        // Update in store
        set((state) => ({
          ...state,
          commitments: state.commitments.map((c) =>
            c.id === commitmentId
              ? {
                  ...c,
                  status: "released" as const,
                  released_at: new Date().toISOString(),
                }
              : c
          ),
        }));

        // Refresh stock store to reflect committed quantity changes
        if (commitment.inventory_id) {
          const stockStore = useStockStore.getState();
          await stockStore.refreshStock(commitment.inventory_id);
        }
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Release all stock commitments for an order
    async releaseAllStockCommitmentsForOrder(orderId: string) {
      set({ loading: true, error: null });
      try {
        await StockCommitmentsClient.releaseAllStockCommitmentsForOrder(orderId);

        // Update in store
        set((state) => ({
          ...state,
          commitments: state.commitments.map((c) =>
            c.order_id === orderId && c.status === "active"
              ? {
                  ...c,
                  status: "released" as const,
                  released_at: new Date().toISOString(),
                }
              : c
          ),
        }));
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Get low stock alerts
    async getLowStockAlerts(threshold: number = 10) {
      set({ loading: true, error: null });
      try {
        const alerts = await StockCommitmentsClient.getLowStockAlerts(threshold);

        // Update store
        set((state) => ({
          ...state,
          stockAvailability: [
            ...state.stockAvailability.filter(
              (a) => !alerts.some((alert) => alert.inventory_id === a.inventory_id)
            ),
            ...alerts,
          ],
        }));

        return alerts;
      } catch (error) {
        set({ error: error as Error });
        throw error;
      } finally {
        set({ loading: false });
      }
    },

    // Clear error
    clearError: () => set({ error: null }),

    // Refresh commitments (useful for debugging or manual refresh)
    async refreshCommitments() {
      set({ loading: true, error: null });
      try {
        // This would typically refresh all commitments, but for now we'll just clear the cache
        set({ commitments: [], stockAvailability: [] });
      } catch (error) {
        set({ error: error as Error });
      } finally {
        set({ loading: false });
      }
    },
  })
);
